import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import lottoApi from "./api";
import decodeSlipQr from "../utils/decodeSlipQr";
import { money2, pushToast } from "./lib";

const STATUS = {
  SUCCESS: { label: "ฝากสำเร็จ", cls: "ok", icon: "solar:check-circle-bold" },
  PENDING: { label: "รอตรวจสอบ", cls: "wait", icon: "solar:clock-circle-bold" },
  FAILED: { label: "ไม่สำเร็จ", cls: "bad", icon: "solar:close-circle-bold" },
};

// ฝากด้วยการอัพโหลดสลิป — อ่าน QR ในเครื่อง แล้วส่งเฉพาะ payload ไปตรวจกับ EasySlip
export default function SlipUpload({ userId, onDone }) {
  const [amount, setAmount] = useState("");
  const [payload, setPayload] = useState("");
  const [preview, setPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    if (userId) setHistory(await lottoApi.slipHistory(userId));
  }, [userId]);

  useEffect(() => {
    lottoApi.slipAccounts().then(setAccounts);
    loadHistory();
  }, [loadHistory]);

  const recipient = accounts?.[0] || null;
  const canSubmit = payload && Number(amount) > 0 && recipient && !busy && !decoding;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setError("");
    setResult(null);
    if (!file) return;
    setDecoding(true);
    setFileName(file.name);
    try {
      const d = await decodeSlipQr(file);
      setPreview(d.previewUrl);
      setPayload(d.payload);
    } catch (err) {
      setPreview("");
      setPayload("");
      setError(err.message || "ไม่สามารถอ่าน QR Code จากภาพได้");
    } finally {
      setDecoding(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await lottoApi.slipVerify({ userId, payload, amount: Number(amount) });
      setResult({ ok: true, message: res?.message || "ส่งตรวจสอบสลิปเรียบร้อยแล้ว" });
      pushToast(res?.message || "ส่งตรวจสอบสลิปแล้ว", "ok");
      setPayload("");
      setPreview("");
      setFileName("");
      loadHistory();
      onDone?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || "ตรวจสอบสลิปไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lx-sp">
      <div className="lx-sp-recipient">
        <div className="lx-dp-label">โอนเข้าบัญชีนี้เท่านั้น แล้วอัพโหลดสลิป</div>
        {accounts === null ? (
          <span className="lx-muted">กำลังโหลดบัญชีรับสลิป...</span>
        ) : recipient ? (
          <div>
            <b>{recipient.bankName}</b>
            <div className="lx-mono lx-sp-acc">{recipient.accountNumber}</div>
            <span className="lx-muted" style={{ fontSize: 13 }}>{recipient.accountName}</span>
          </div>
        ) : (
          <span style={{ color: "var(--lx-red)" }}>ยังไม่มีบัญชีรับสลิปที่เปิดใช้งาน กรุณาติดต่อเจ้าหน้าที่</span>
        )}
      </div>

      <label className="lx-sp-label">จำนวนเงินที่โอน (บาท)</label>
      <input
        className="lx-sp-input"
        inputMode="decimal"
        placeholder="เช่น 300"
        value={amount}
        disabled={busy}
        onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
      />

      <label className={`lx-sp-drop ${preview ? "has" : ""}`}>
        <input type="file" accept="image/*" onChange={onFile} disabled={decoding || busy} hidden />
        {preview ? (
          <img src={preview} alt="สลิป" />
        ) : (
          <Icon icon="solar:upload-square-bold-duotone" width={34} height={34} />
        )}
        <b>{decoding ? "กำลังอ่าน QR..." : preview ? "เปลี่ยนรูปสลิป" : "เลือกรูปสลิป"}</b>
        <span className="lx-muted" style={{ fontSize: 12 }}>
          {fileName || "รองรับ JPG / PNG / WEBP — อ่าน QR ในเครื่อง ไม่อัพโหลดรูปขึ้นเซิร์ฟเวอร์"}
        </span>
      </label>

      {error && <div className="lx-sp-msg bad">{error}</div>}
      {result?.ok && <div className="lx-sp-msg ok">{result.message}</div>}

      <button className="lx-btn gradient block" disabled={!canSubmit} onClick={submit}>
        {busy ? "กำลังตรวจสอบสลิป..." : "ยืนยันส่งสลิป"}
      </button>

      <h3 className="lx-h2" style={{ margin: "20px 0 8px" }}>ประวัติอัพโหลดสลิป</h3>
      {!history.length ? (
        <div className="lx-muted" style={{ fontSize: 13 }}>ยังไม่มีประวัติ</div>
      ) : (
        <div className="lx-sp-list">
          {history.map((h) => {
            const st = STATUS[h.status] || STATUS.FAILED;
            return (
              <div className="lx-sp-item" key={h.id}>
                <span className={`lx-sp-badge ${st.cls}`}>
                  <Icon icon={st.icon} width={14} height={14} /> {st.label}
                </span>
                <b className="lx-mono">{h.amount != null ? money2(h.amount) : "-"}</b>
                <span className="lx-muted lx-sp-when">
                  {String(h.created_at || "").replace("T", " ").slice(0, 16)}
                </span>
                {h.error_message && <span className="lx-sp-note">{h.error_message}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
